import React, { createContext, useContext, useMemo, useReducer } from "react";

import { STAGE_SOURCE, isStageLocked } from "../components/stages";

// Single context + reducer covering three slices: session, chat, workingPanel.
// Mirrors the POC's three Zustand stores collapsed into one place — keeps the dep footprint
// small (no Zustand) while preserving the slice boundaries documented in the POC.

const initialState = {
  session: {
    sessionId: null,
    status: "created",
    hasFile: false,
    sourceFileName: null,
    // The data task this session writes into, and which part of it. Sourced from the SESSION response,
    // never from the URL: a bookmarked link can carry a stale or hand-edited `?target=`, and the server
    // silently drops one it does not recognise. Committing against a destination the agent never
    // validated is the failure this prevents.
    questionnaireId: null,
    aiIngestTarget: null,
    // Whether the agent has read the task's output structure yet. Without it there are no row paths, so
    // there is nothing to commit. This is the ONLY structure a session carries — the user-defined output
    // template that used to sit alongside it is gone, because the task publishes its own.
    hasTaskTemplate: false,
    // "questionnaire" (a carbon data task) or "foundation_v2" (a Foundation Data v2 year). Defaults to
    // the carbon kind, so a session loaded by an older client behaves exactly as it did before.
    sessionKind: "questionnaire",
    foundationYearId: null,
  },
  chat: {
    messages: [],
    isConnected: false,
    isThinking: false,
    // "The agent is mid-turn", which is NOT what `isThinking` means. `isThinking` drives the "Thinking…"
    // indicator and is switched off by the first streamed token, so it is false for most of a turn that is
    // very much still running. This one goes true on `thinking` and false only on `response`/`error` — the
    // events that actually end a turn — because the auto-mapping needs to wait for a turn to FINISH, and
    // firing on the first token would queue a second turn behind the session write lock the first holds.
    isTurnActive: false,
    inputTokens: 0,
    outputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
  },
  workingPanel: {
    isOpen: true,
    // Which pipeline stage the panel is showing. Shared rather than local to WorkingPanel because two
    // other places need it: ChatPanel closes the composer on the read-only stages, and SessionLayout
    // starts the AI mapping when the user arrives at Map & review. Starts on Upload & view; SessionLayout
    // moves it to Preview & transform on load when the session already has data (see its own comment —
    // a resumed session, and every foundation session, would otherwise land on a locked stage).
    stage: STAGE_SOURCE,
    dataframes: [],
    selectedKey: null,
    tableData: null,
    currentPage: 1,
    totalPages: 1,
    isLoadingList: false,
    isLoadingData: false,
    refreshCounter: 0,
    selectionMode: null,
    selectedColumns: [],
    selectedRows: [],
    selectedCells: [],
    lastAnchor: null,
    charts: [],
    isLoadingCharts: false,
    // Two distinct counters, not one: chartsRefreshCounter drives a plain
    // refetch (e.g. on data_modified — must NOT steal focus from a table the
    // user is reading), while chartCreatedCounter also switches the panel to
    // the Charts view (only when a NEW chart was just created).
    chartsRefreshCounter: 0,
    chartCreatedCounter: 0,
    // Bumped when the agent creates the frame the user reviews, and only then — so the panel moves to
    // Map & review the moment there is something to review. A counter rather than a boolean for the same
    // reason as chartCreatedCounter: the second output frame of a session has to move the panel too, and a
    // flag that is already true cannot.
    outputCreatedCounter: 0,
    // Which mapped "_output" frame the user is reviewing on the Preview/Commit stages. Shared state rather
    // than local to OutputFrameTabs, because the commit rail has to commit *that* frame — the one that was
    // reviewed. Inferring "the newest output" there would let the two diverge silently.
    selectedOutputKey: null,
  },
};

let messageIdCounter = 0;
const nextMessageId = () => `msg-${++messageIdCounter}`;

function rangeBetween(a, b) {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}

function toggleInArray(arr, val) {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

const EMPTY_SELECTION = {
  selectionMode: null,
  selectedColumns: [],
  selectedRows: [],
  selectedCells: [],
  lastAnchor: null,
};

function sessionReducer(state, action) {
  switch (action.type) {
    case "SET_SESSION":
      return {
        ...state,
        sessionId: action.id,
        status: action.status,
        questionnaireId: action.questionnaireId ?? null,
        aiIngestTarget: action.aiIngestTarget ?? null,
        hasTaskTemplate: Boolean(action.hasTaskTemplate),
        // Which kind of work item this session belongs to. Held here rather than inferred from
        // `questionnaireId` being null: the panels branch on it, and "no task" and "a foundation year"
        // are different states that would otherwise look identical.
        sessionKind: action.sessionKind ?? "questionnaire",
        foundationYearId: action.foundationYearId ?? null,
      };
    case "SET_FILE":
      return { ...state, hasFile: action.hasFile, sourceFileName: action.name };
    case "SET_STATUS":
      return { ...state, status: action.status };
    default:
      return state;
  }
}

// Exported for tests: the streaming-bubble logic below is the kind that only misbehaves under timing
// the UI cannot easily be driven into, and it is a pure function, so it is asserted directly.
export function chatReducer(state, action) {
  switch (action.type) {
    case "ADD_MESSAGE":
      return {
        ...state,
        messages: [
          ...state.messages,
          { ...action.message, id: nextMessageId(), timestamp: Date.now() },
        ],
      };
    // "Start a new bubble or extend the current one?" is decided HERE, not by the caller.
    //
    // It used to be decided in useDataMappingWebSocket's `token` handler, which read `messages` off a
    // ref holding the LAST RENDER's snapshot. Streaming tokens arrive far faster than React re-renders,
    // so every chunk in a burst saw the same stale "there is no streaming message yet" and called
    // ADD_MESSAGE again — one bubble per chunk, splitting mid-word ("all k" + "Wh"), each with its own
    // streaming cursor. A reducer cannot have that bug: React applies reducers against current state,
    // so within one batch each dispatch sees the previous one's result.
    case "APPEND_TOKEN": {
      const msgs = [...state.messages];
      const last = msgs[msgs.length - 1];
      if (last?.role === "assistant" && last.isStreaming) {
        msgs[msgs.length - 1] = { ...last, content: last.content + action.token };
        return { ...state, messages: msgs };
      }
      // `isStreaming` in that test is load-bearing, not defensive: without it a token from a NEW turn
      // appends to the previous turn's finished reply.
      return {
        ...state,
        messages: [
          ...msgs,
          {
            role: "assistant",
            content: action.token,
            isStreaming: true,
            id: nextMessageId(),
            timestamp: Date.now(),
          },
        ],
      };
    }
    // Same reasoning: finalise the in-flight reply, or create it if no token ever arrived (a turn that
    // produced only a tool call and a final answer). Deciding that in the handler risked appending a
    // duplicate when `response` was batched with the tokens it concludes.
    case "RESOLVE_ASSISTANT": {
      const msgs = [...state.messages];
      const last = msgs[msgs.length - 1];
      if (last?.role === "assistant" && last.isStreaming) {
        msgs[msgs.length - 1] = { ...last, content: action.content, isStreaming: false };
        return { ...state, messages: msgs };
      }
      return {
        ...state,
        messages: [
          ...msgs,
          {
            role: "assistant",
            content: action.content,
            isStreaming: false,
            id: nextMessageId(),
            timestamp: Date.now(),
          },
        ],
      };
    }
    case "UPDATE_CODE_EXECUTION_OUTPUT": {
      const msgs = [...state.messages];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].codeExecution) {
          msgs[i] = {
            ...msgs[i],
            codeExecution: { ...msgs[i].codeExecution, output: action.output },
          };
          break;
        }
      }
      return { ...state, messages: msgs };
    }
    case "SET_MESSAGES":
      return {
        ...state,
        messages: action.messages.map((m) => ({
          id: nextMessageId(),
          role: m.role,
          content: m.content,
          timestamp: Date.now(),
        })),
      };
    case "SET_CONNECTED":
      // A dropped socket ends what we KNOW about the turn — the server may well still be running it (see
      // "Closing the tab does not cancel a turn" in data-sorting/CLAUDE.md), but no `response` will ever
      // reach us, so leaving the flag set would wedge anything waiting on it for the life of the page.
      return {
        ...state,
        isConnected: action.connected,
        isTurnActive: action.connected ? state.isTurnActive : false,
      };
    case "SET_THINKING":
      return { ...state, isThinking: action.thinking };
    case "SET_TURN_ACTIVE":
      return { ...state, isTurnActive: action.active };
    case "SET_TOKEN_USAGE":
      return {
        ...state,
        inputTokens: action.input,
        outputTokens: action.output,
        cacheReadTokens: action.cacheRead,
        cacheWriteTokens: action.cacheWrite,
      };
    default:
      return state;
  }
}

function workingPanelReducer(state, action) {
  switch (action.type) {
    case "TOGGLE_PANEL":
      return { ...state, isOpen: !state.isOpen };
    case "SET_STAGE":
      // Selection is cleared on the way into a read-only stage, not on every stage change: it is only
      // meaningful as context for a message, and the composer is closed there — leaving it would put a
      // "3 columns selected" chip above a dead input, offering to do something that cannot be done.
      return {
        ...state,
        stage: action.stage,
        ...(isStageLocked(action.stage) ? EMPTY_SELECTION : null),
      };
    case "SET_DATAFRAMES":
      return { ...state, dataframes: action.dataframes };
    case "SET_SELECTED_KEY":
      return { ...state, selectedKey: action.key, currentPage: 1, ...EMPTY_SELECTION };
    case "SET_SELECTED_OUTPUT_KEY":
      return { ...state, selectedOutputKey: action.key };
    case "SET_TABLE_DATA":
      return { ...state, tableData: action.data };
    case "SET_PAGE":
      return { ...state, currentPage: action.page };
    case "SET_TOTAL_PAGES":
      return { ...state, totalPages: action.totalPages };
    case "SET_LOADING_LIST":
      return { ...state, isLoadingList: action.loading };
    case "SET_LOADING_DATA":
      return { ...state, isLoadingData: action.loading };
    case "TRIGGER_REFRESH":
      return { ...state, refreshCounter: state.refreshCounter + 1, ...EMPTY_SELECTION };
    case "SELECT_COLUMN": {
      const { index, extend, toggle } = action;
      if (extend && state.lastAnchor !== null && state.selectionMode === "columns") {
        return {
          ...state,
          selectionMode: "columns",
          selectedColumns: rangeBetween(state.lastAnchor, index),
          selectedRows: [],
          selectedCells: [],
        };
      }
      if (toggle && state.selectionMode === "columns") {
        const cols = toggleInArray(state.selectedColumns, index);
        return {
          ...state,
          selectionMode: cols.length > 0 ? "columns" : null,
          selectedColumns: cols,
          selectedRows: [],
          selectedCells: [],
          lastAnchor: index,
        };
      }
      return {
        ...state,
        selectionMode: "columns",
        selectedColumns: [index],
        selectedRows: [],
        selectedCells: [],
        lastAnchor: index,
      };
    }
    case "SELECT_ROW": {
      const { index, extend, toggle } = action;
      if (extend && state.lastAnchor !== null && state.selectionMode === "rows") {
        return {
          ...state,
          selectionMode: "rows",
          selectedRows: rangeBetween(state.lastAnchor, index),
          selectedColumns: [],
          selectedCells: [],
        };
      }
      if (toggle && state.selectionMode === "rows") {
        const rows = toggleInArray(state.selectedRows, index);
        return {
          ...state,
          selectionMode: rows.length > 0 ? "rows" : null,
          selectedRows: rows,
          selectedColumns: [],
          selectedCells: [],
          lastAnchor: index,
        };
      }
      return {
        ...state,
        selectionMode: "rows",
        selectedRows: [index],
        selectedColumns: [],
        selectedCells: [],
        lastAnchor: index,
      };
    }
    case "SELECT_CELL": {
      const { row, col, toggle } = action;
      const ref = { row, col };
      if (toggle && state.selectionMode === "cells") {
        const exists = state.selectedCells.some((c) => c.row === row && c.col === col);
        const cells = exists
          ? state.selectedCells.filter((c) => !(c.row === row && c.col === col))
          : [...state.selectedCells, ref];
        return {
          ...state,
          selectionMode: cells.length > 0 ? "cells" : null,
          selectedCells: cells,
          selectedColumns: [],
          selectedRows: [],
        };
      }
      return {
        ...state,
        selectionMode: "cells",
        selectedCells: [ref],
        selectedColumns: [],
        selectedRows: [],
      };
    }
    case "CLEAR_SELECTION":
      return { ...state, ...EMPTY_SELECTION };
    case "SET_CHARTS":
      return { ...state, charts: action.charts };
    case "SET_LOADING_CHARTS":
      return { ...state, isLoadingCharts: action.loading };
    case "REFRESH_CHARTS":
      return { ...state, chartsRefreshCounter: state.chartsRefreshCounter + 1 };
    case "CHART_CREATED":
      return {
        ...state,
        chartsRefreshCounter: state.chartsRefreshCounter + 1,
        chartCreatedCounter: state.chartCreatedCounter + 1,
      };
    case "OUTPUT_CREATED":
      // Refreshes the frame list too: the new output frame has to be in `dataframes` before the view that
      // renders it becomes visible, or Map & review paints empty for a frame that exists.
      return {
        ...state,
        refreshCounter: state.refreshCounter + 1,
        outputCreatedCounter: state.outputCreatedCounter + 1,
      };
    default:
      return state;
  }
}

function rootReducer(state, action) {
  if (action.type === "RESET") return initialState;
  if (action.slice === "session") return { ...state, session: sessionReducer(state.session, action) };
  if (action.slice === "chat") return { ...state, chat: chatReducer(state.chat, action) };
  if (action.slice === "workingPanel") {
    return { ...state, workingPanel: workingPanelReducer(state.workingPanel, action) };
  }
  return state;
}

const DataMappingContext = createContext(null);

export function DataMappingProvider({ children }) {
  const [state, dispatch] = useReducer(rootReducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <DataMappingContext.Provider value={value}>{children}</DataMappingContext.Provider>;
}

export function useDataMapping() {
  const ctx = useContext(DataMappingContext);
  if (!ctx) throw new Error("useDataMapping must be used inside DataMappingProvider");
  return ctx;
}
